// AccessControlHOC.js
import React, { useEffect, useState } from "react";
import { checkUserDetailsFromCookie, pageAccessAllowed } from "../../../api_calls/userManagement";
import { Box, Button, Grid, IconButton, Typography } from "@mui/material";
import SideNav from "../Sidebar/SideNav";
import { StyledMainBox } from "../StyledComponents/styled";
import forbiddenImage from "../../../images/forbidden/accessForbidden.png";
import { deletePaginationCookies, verifyBrowserName } from "../../../api_calls/utils";
import { toast } from "react-toastify";
import { checkBackendHealth, createCookie, fetchBrowserNotificationCookie } from "../../../api_calls/requestConfig";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { shouldShowAllRetailersOnlyWorkspaceNotice } from "../../../api_calls/retailerSelection";
import {
  BROWSER_COMPAT_TOAST_OPTIONS,
  getRetailerScopeToastOptions,
  isRetailerScopeNoticeDismissed,
  RetailerScopeNotice,
  RETAILER_SCOPE_TOAST_ID,
} from "./accessControlToasts";

const AccessControl = (WrappedComponent, permissionsToCheck, allowedUserTypes, sectionCheck, accessFor) => {
  return function AccessControl(props) {
    const [canAccess, setCanAccess] = useState("checking");
    const appContextRefreshVersion = useSelector((state) => state.userSelections.app_context_refresh_version || 0);
    const bottomNavRetailerSelection = useSelector((state) => state.userSelections.bottom_nav_selected_retailer);
    const retailerSelectionOptions = useSelector((state) => state.userSelections.retailer_selection_options);
    const browserName = verifyBrowserName();
    const browserCookie = fetchBrowserNotificationCookie();
    const checkUserDetails = checkUserDetailsFromCookie();
    const userType = checkUserDetails.success === true ? checkUserDetails.user_detail.user_type : "";
    let deactivatedSections = checkUserDetails.success === true ? checkUserDetails.user_detail.deactivated_sections : [];
    const pathName = window.location.pathname;
    const preDefinedPathNames = ["infrastructure", "check-campaigns", "cb-visuals"];

    const navigate = useNavigate();

    useEffect(() => {
      checkBackendHealth(navigate).then(info => {
        if (info.success !== false) {
          // If backend is healthy, proceed with access control checks
          const checkAccess = pageAccessAllowed(permissionsToCheck, allowedUserTypes);
          // sectionCheck is an array that needs to be checked for access against deactivatedSections (if any)
          const sectionAccess = typeof sectionCheck !== "undefined" ? userType === "cyreen" || sectionCheck.every(access_name => !deactivatedSections.includes(access_name)) : true;
          // Access control can only be passed if both checkAccess and sectionAccess are true
          if (checkAccess.success && sectionAccess) {
            setCanAccess(true);
          } else {
            setCanAccess(false);
          }
          // setCanAccess(checkAccess.success);

          if (browserName !== "Google Chrome" && browserCookie === "") {
            toast.dismiss();
            toast(
              "CAP Explorer is not optimized for the browser you are currently using, potentially limiting some functionalities and affecting visualizations. For an optimal user experience and performance, we recommend to use Google Chrome.",
              {
                ...BROWSER_COMPAT_TOAST_OPTIONS,
                onClose: () => {
                  createCookie("browserNotification", "shown");
                },
              }
            );
          }

          if (!preDefinedPathNames.some((path) => pathName.includes(path))) {
            deletePaginationCookies();
          }
        }
      });
    }, [permissionsToCheck]);

    useEffect(() => {
      if (canAccess !== true) {
        if (canAccess === false) {
          toast.dismiss(RETAILER_SCOPE_TOAST_ID);
        }
        return;
      }

      const userSelections = {
        bottom_nav_selected_retailer: bottomNavRetailerSelection,
        retailer_selection_options: retailerSelectionOptions,
      };
      const shouldShowNotice = shouldShowAllRetailersOnlyWorkspaceNotice(userType, userSelections);
      const retailerName =
        typeof bottomNavRetailerSelection !== "undefined" && typeof bottomNavRetailerSelection.label === "string"
          ? bottomNavRetailerSelection.label
          : "";

      if (shouldShowNotice === true && isRetailerScopeNoticeDismissed() !== true) {
        const notice = <RetailerScopeNotice retailerName={retailerName} />;
        if (toast.isActive(RETAILER_SCOPE_TOAST_ID) === true) {
          toast.update(RETAILER_SCOPE_TOAST_ID, { render: notice });
        } else {
          toast(notice, getRetailerScopeToastOptions());
        }
      } else {
        toast.dismiss(RETAILER_SCOPE_TOAST_ID);
      }
    }, [
      appContextRefreshVersion,
      bottomNavRetailerSelection,
      canAccess,
      retailerSelectionOptions,
      userType,
    ]);

    return (
      <Box component="div" sx={{ display: "flex" }}>
        <SideNav accessFor={accessFor} />

        {canAccess === true ? (
          <>
            {/* <AnnouncementWidget /> */}
            <WrappedComponent key={appContextRefreshVersion} {...props} />
          </>
        ) : (
          <>
            {canAccess === false && (
              <StyledMainBox component="main">
                <Box height={300} />
                <Box height={100} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                  <img src={forbiddenImage} height={500} width={700} />
                  <Typography variant="h4" color="#AF3241" sx={{ marginBottom: 2 }}>
                    No permission
                  </Typography>
                  {/* <Typography variant="h5" color="#AF3241">
                  Access Denied: You do not have permission to view this page.
                </Typography> */}
                </Box>
              </StyledMainBox>
            )}
          </>
        )}

      </Box>
    );
  };
};

export default AccessControl;
